import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import GridOnIcon from '@mui/icons-material/GridOn';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { v4 as uuid } from 'uuid';

import type { CourseTeeSetData, EventScoreWithId, scoreHole } from '../../../shared/config/iTournamentBuilder';
import { ImageLibrary } from '../../../shared/Components/ImageLibrary';
import { ScorecardCardItem } from './ScorecardCardItem';
import ScorecardGrid from './ScorecardGrid';
import {
    ONE_INCH_H_PCT,
    SCORECARD_BINDING_TOKENS,
    SCORECARD_CANVAS_H,
    SCORECARD_CANVAS_W,
    resolveScorecardBindings,
    resolveHighlightHoleNumber,
    templateUsesStartingHoleBinding,
} from './scorecardTemplateTypes';
import type {
    ScorecardElement,
    ScorecardGridConfig,
    ScorecardTemplate,
    ScoringUnit,
    TextAlign,
} from './scorecardTemplateTypes';

// ─── Canvas aliases ───────────────────────────────────────────────────────────

const CANVAS_W = SCORECARD_CANVAS_W; // 792
const CANVAS_H = SCORECARD_CANVAS_H; // 306

// ─── Zoom constants ───────────────────────────────────────────────────────────

const MIN_ZOOM  = 0.25;
const MAX_ZOOM  = 3.0;
const ZOOM_STEP = 0.25;

// ─── Props ────────────────────────────────────────────────────────────────────

interface ScorecardTemplateDesignerProps {
    open: boolean;
    template: ScorecardTemplate;
    /** First active scoring unit used for live preview bindings. */
    previewScoringUnit: ScoringUnit | null;
    /** All scoring units for the event — used to resolve marker names across units. */
    allScoringUnits?: ScoringUnit[];
    previewEventData: Record<string, unknown>;
    previewCourseData: CourseTeeSetData | null;
    previewScores: EventScoreWithId[];
    roundValue: number;
    onImageUpload?: (file: File) => Promise<string>;
    onSave: (template: ScorecardTemplate) => void;
    onPrintTest: (opts: {
        template: ScorecardTemplate;
        scoringUnit: ScoringUnit;
        showScores: boolean;
        blankMode: boolean;
    }) => Promise<void>;
    onClose: () => void;
}

// ─── Drag / resize state ──────────────────────────────────────────────────────

interface DragState {
    elementId: string;
    startMouseX: number;
    startMouseY: number;
    startX: number;
    startY: number;
    markerPosition?: 'top' | 'bottom';
    elementHeight: number;
}

interface ResizeState {
    elementId: string;
    startMouseX: number;
    startMouseY: number;
    startW: number;
    startH: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(val: number, min: number, max: number) {
    return Math.max(min, Math.min(max, val));
}

function pxToPercX(px: number) { return (px / CANVAS_W) * 100; }
function pxToPercY(px: number) { return (px / CANVAS_H) * 100; }

function elementDisplayName(el: ScorecardElement, idx: number, all: ScorecardElement[]): string {
    const label =
        el.type === 'text'          ? 'Text' :
        el.type === 'image'         ? 'Image' :
        el.type === 'qrcode'        ? 'QR Code' :
        el.type === 'line'          ? 'Line' :
        el.type === 'scorecardGrid' ? (el.gridConfig?.gridRole === 'marker' ? 'Marker Grid' : 'Scorecard Grid') :
        'Element';
    const sameTypeBefore = all.slice(0, idx).filter((e) => e.type === el.type).length;
    const totalOfType    = all.filter((e) => e.type === el.type).length;
    return totalOfType === 1 ? label : `${label} ${sameTypeBefore + 1}`;
}

function layerIcon(el: ScorecardElement): React.ElementType {
    switch (el.type) {
        case 'text':          return TextFieldsIcon;
        case 'image':         return ImageOutlinedIcon;
        case 'qrcode':        return QrCode2Icon;
        case 'line':          return HorizontalRuleIcon;
        case 'scorecardGrid': return GridOnIcon;
        default:              return TextFieldsIcon;
    }
}

function defaultGridConfig(role: 'player' | 'marker' = 'player'): ScorecardGridConfig {
    return {
        showPar:      true,
        showHandicap: true,
        showDistance: true,
        showPlayer:   true,
        playerRows:   'auto',
        showMarker:   false,
        showStrokes:  false,
        showFoldGap:  true,
        foldGapPx:    16,
        gridRole:     role,
    };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const ScorecardTemplateDesigner: React.FC<ScorecardTemplateDesignerProps> = ({
    open,
    template: initialTemplate,
    previewScoringUnit,
    allScoringUnits,
    previewEventData,
    previewCourseData,
    previewScores,
    roundValue,
    onImageUpload,
    onSave,
    onPrintTest,
    onClose,
}) => {
    const [template, setTemplate]       = useState<ScorecardTemplate>(() =>
        JSON.parse(JSON.stringify(initialTemplate)),
    );
    const [selectedId, setSelectedId]   = useState<string | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [previewShowScores, setPreviewShowScores] = useState(false);
    const [previewBlankMode, setPreviewBlankMode]   = useState(false);
    const [testPrinting, setTestPrinting]           = useState(false);
    const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set());
    const [zoom, setZoom]               = useState(1.0);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [libraryContext, setLibraryContext] = useState<'add' | string>('add');

    const canvasRef          = useRef<HTMLDivElement>(null);
    const dragRef            = useRef<DragState | null>(null);
    const resizeRef          = useRef<ResizeState | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const zoomRef            = useRef(zoom);
    React.useEffect(() => { zoomRef.current = zoom; }, [zoom]);

    // ── Non-passive wheel listener ────────────────────────────────────────────
    // Depends on `open`: MUI Dialog children aren't in the DOM until the first
    // time open transitions to true, so the ref is null on the initial mount run.
    React.useEffect(() => {
        if (!open) return;
        const el = scrollContainerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            setZoom((prev) => clamp(prev * (e.deltaY < 0 ? 1.1 : 0.9), MIN_ZOOM, MAX_ZOOM));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [open]);

    // ── Reset on open ─────────────────────────────────────────────────────────
    React.useEffect(() => {
        if (open) {
            setTemplate(JSON.parse(JSON.stringify(initialTemplate)));
            setSelectedId(null);
            setShowPreview(false);
            setZoom(1.0);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedElement = template.elements.find((el) => el.id === selectedId) ?? null;

    const previewUnitScores = useMemo<EventScoreWithId | null>(() => {
        if (!previewShowScores || !previewScoringUnit?.eventScoreID) return null;
        return previewScores.find(
            (s) => String((s as any)._id) === String(previewScoringUnit.eventScoreID),
        ) ?? null;
    }, [previewShowScores, previewScoringUnit, previewScores]);

    const { previewMarkerScores, previewMarkerLabel, previewMarkerScoreCode } = useMemo<{
        previewMarkerScores: scoreHole[] | null;
        previewMarkerLabel: string;
        previewMarkerScoreCode: string;
    }>(() => {
        if (!previewShowScores || !previewUnitScores) {
            return { previewMarkerScores: null, previewMarkerLabel: '', previewMarkerScoreCode: '' };
        }
        const roundScore = previewUnitScores.roundScores?.find(
            (rs) => rs.roundNumber === roundValue,
        );
        const markerScoreCode = (roundScore as any)?.markerScoreCode ?? null;
        if (!markerScoreCode) {
            return { previewMarkerScores: null, previewMarkerLabel: '', previewMarkerScoreCode: '' };
        }
        const markerEventScore = previewScores.find((s) =>
            s.roundScores?.some(
                (rs) => rs.roundNumber === roundValue && rs.playerScoreCode === markerScoreCode,
            ),
        );
        const holeScores = markerEventScore?.roundScores.find(
            (rs) => rs.roundNumber === roundValue,
        )?.holeScores ?? [];
        // Collect all names for the marker score code across all scoring units (mirrors buildScoringUnits playerName)
        const unitsToSearch = allScoringUnits ?? (previewScoringUnit ? [previewScoringUnit] : []);
        const markerNames: string[] = [];
        for (const u of unitsToSearch) {
            for (const reg of u.registrations) {
                if (reg.scoreCode === markerScoreCode) {
                    markerNames.push(reg.playerName ?? '');
                }
            }
        }
        const markerLabel = markerNames.filter(Boolean).join(' / ');
        return {
            previewMarkerScores: holeScores.length > 0 ? holeScores : null,
            previewMarkerLabel: markerLabel,
            previewMarkerScoreCode: markerScoreCode,
        };
    }, [previewShowScores, previewUnitScores, previewScores, previewScoringUnit, allScoringUnits, roundValue]);

    const handleTestPrint = async () => {
        if (!previewScoringUnit) return;
        setTestPrinting(true);
        try {
            await onPrintTest({
                template: JSON.parse(JSON.stringify(template)),
                scoringUnit: previewScoringUnit,
                showScores: previewShowScores,
                blankMode: previewBlankMode,
            });
        } finally {
            setTestPrinting(false);
        }
    };

    // ── Element mutation helpers ──────────────────────────────────────────────

    const updateElements = useCallback(
        (updater: (els: ScorecardElement[]) => ScorecardElement[]) => {
            setTemplate((prev) => ({ ...prev, elements: updater(prev.elements) }));
        },
        [],
    );

    const updateElement = useCallback(
        (id: string, patch: Partial<ScorecardElement>) => {
            updateElements((els) =>
                els.map((el) => (el.id === id ? { ...el, ...patch } : el)),
            );
        },
        [updateElements],
    );

    const addElement = (partial: Omit<ScorecardElement, 'id'>) => {
        const el: ScorecardElement = { id: uuid(), ...partial };
        updateElements((els) => [...els, el]);
        setSelectedId(el.id);
    };

    const deleteSelected = () => {
        if (!selectedId) return;
        updateElements((els) => els.filter((el) => el.id !== selectedId));
        setSelectedId(null);
    };

    // ── Add helpers ───────────────────────────────────────────────────────────

    const handleAddText = () =>
        addElement({
            type: 'text', x: 1, y: 1, width: 50, height: 8,
            content: 'Text', fontSize: 11, fontWeight: 'normal', color: '#000000', textAlign: 'left',
        });

    const handleAddLine = () =>
        addElement({
            type: 'line', x: 1, y: 50, width: 98, height: 0.5,
            lineOrientation: 'horizontal', lineColor: '#000000', lineThickness: 1,
        });

    const handleAddQrCode = () =>
        addElement({ type: 'qrcode', x: 75, y: 20, width: 20, height: 50 });

    const handleAddScorecardGrid = (role: 'player' | 'marker' = 'player') =>
        addElement({
            type: 'scorecardGrid', x: 0, y: 16, width: 100, height: 62,
            gridConfig: defaultGridConfig(role),
        });

    // ── Image library ─────────────────────────────────────────────────────────

    const openLibraryForAdd     = () => { setLibraryContext('add');   setLibraryOpen(true); };
    const openLibraryForReplace = (id: string) => { setLibraryContext(id); setLibraryOpen(true); };

    const handleLibrarySelect = ({ url }: { url: string; displayName: string }) => {
        if (libraryContext === 'add') {
            const el: ScorecardElement = { id: uuid(), type: 'image', x: 5, y: 5, width: 30, height: 30, imageDataUrl: url };
            updateElements((els) => [...els, el]);
            setSelectedId(el.id);
        } else {
            updateElement(libraryContext, { imageDataUrl: url });
        }
        setLibraryOpen(false);
    };

    // ── Image replace (upload) ────────────────────────────────────────────────

    const handleImageReplace = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !selectedElement) return;
        const elementId = selectedElement.id;
        const reader = new FileReader();
        reader.onload = async (evt) => {
            const base64 = evt.target?.result as string;
            updateElement(elementId, { imageDataUrl: base64 });
            if (onImageUpload) {
                setUploadingIds((prev) => new Set(prev).add(elementId));
                try {
                    const cdnUrl = await onImageUpload(file);
                    updateElement(elementId, { imageDataUrl: cdnUrl });
                } catch (err) {
                    console.error('Image upload failed — keeping local preview:', err);
                } finally {
                    setUploadingIds((prev) => { const s = new Set(prev); s.delete(elementId); return s; });
                }
            }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    // ── Drag ──────────────────────────────────────────────────────────────────

    const handleElementMouseDown = (e: React.MouseEvent<HTMLDivElement>, el: ScorecardElement) => {
        e.stopPropagation();
        setSelectedId(el.id);
        dragRef.current = {
            elementId: el.id,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startX: el.x,
            startY: el.y,
            markerPosition: el.gridConfig?.markerPosition,
            elementHeight: el.height,
        };

        const onMove = (me: MouseEvent) => {
            if (!dragRef.current) return;
            const dx = (me.clientX - dragRef.current.startMouseX) / zoomRef.current;
            const dy = (me.clientY - dragRef.current.startMouseY) / zoomRef.current;
            const rawX = dragRef.current.startX + pxToPercX(dx);
            const rawY = dragRef.current.startY + pxToPercY(dy);

            let clampedY: number;
            const mp = dragRef.current.markerPosition;
            if (mp === 'top') {
                clampedY = clamp(rawY, 0, ONE_INCH_H_PCT - dragRef.current.elementHeight);
            } else if (mp === 'bottom') {
                clampedY = clamp(rawY, 100 - ONE_INCH_H_PCT, 100 - dragRef.current.elementHeight);
            } else {
                clampedY = clamp(rawY, 0, 100);
            }
            updateElement(dragRef.current.elementId, {
                x: clamp(rawX, 0, 100),
                y: clampedY,
            });
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // ── Resize ────────────────────────────────────────────────────────────────

    const handleResizeMouseDown = (e: React.MouseEvent<HTMLDivElement>, el: ScorecardElement) => {
        e.stopPropagation();
        resizeRef.current = {
            elementId: el.id,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startW: el.width,
            startH: el.height,
        };
        const onMove = (me: MouseEvent) => {
            if (!resizeRef.current) return;
            const dx = (me.clientX - resizeRef.current.startMouseX) / zoomRef.current;
            const dy = (me.clientY - resizeRef.current.startMouseY) / zoomRef.current;
            updateElement(resizeRef.current.elementId, {
                width:  clamp(resizeRef.current.startW + pxToPercX(dx), 0.5, 100),
                height: clamp(resizeRef.current.startH + pxToPercY(dy), 0.5, 100),
            });
        };
        const onUp = () => {
            resizeRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const handleCanvasClick = () => setSelectedId(null);

    const handleBindingInsert = (token: string) => {
        if (!selectedElement || selectedElement.type !== 'text') return;
        updateElement(selectedElement.id, { content: (selectedElement.content ?? '') + token });
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
            PaperProps={{ sx: { height: '90vh', maxHeight: '90vh', overflow: 'hidden' } }}>

            <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                    Scorecard Template Designer
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                    {CANVAS_W} × {CANVAS_H} px · 11″ × 4.25″
                </Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex' }}>
                <Stack direction="row" sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>

                    {/* ── Left: Palette ─────────────────────────────────────── */}
                    <Stack spacing={1.5} sx={{
                        width: 200, flexShrink: 0,
                        borderRight: '1px solid', borderColor: 'divider',
                        p: 2, overflowY: 'auto',
                    }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Add Elements
                        </Typography>

                        <Button variant="outlined" size="small" startIcon={<TextFieldsIcon />}
                            onClick={handleAddText} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                            Text Field
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<ImageOutlinedIcon />}
                            onClick={openLibraryForAdd} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                            Image Library
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<QrCode2Icon />}
                            onClick={handleAddQrCode} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                            QR Code
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<HorizontalRuleIcon />}
                            onClick={handleAddLine} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                            Line
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<GridOnIcon />}
                            onClick={() => handleAddScorecardGrid('player')}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                            Player Grid
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<GridOnIcon />}
                            onClick={() => handleAddScorecardGrid('marker')}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                            Marker Grid
                        </Button>

                        <Divider />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Background
                        </Typography>
                        <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                            <Box sx={{
                                width: 28, height: 28, borderRadius: 1,
                                border: '1px solid rgba(0,0,0,0.25)',
                                backgroundColor: template.backgroundColor, flexShrink: 0,
                            }} />
                            <Typography variant="caption">{template.backgroundColor}</Typography>
                            <input type="color" value={template.backgroundColor}
                                onChange={(e) => setTemplate((prev) => ({ ...prev, backgroundColor: e.target.value }))}
                                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                            />
                        </Box>

                        <Divider />

                        <Button variant="text" size="small"
                            onClick={() => setShowPreview((v) => !v)}
                            sx={{ textTransform: 'none' }}>
                            {showPreview ? 'Edit Mode' : 'Preview Mode'}
                        </Button>

                        <Divider />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Preview Options
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={previewShowScores}
                                    onChange={(e) => setPreviewShowScores(e.target.checked)}
                                />
                            }
                            label={<Typography variant="caption">Show Scores</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={previewBlankMode}
                                    onChange={(e) => setPreviewBlankMode(e.target.checked)}
                                />
                            }
                            label={<Typography variant="caption">Print Blank</Typography>}
                        />
                    </Stack>

                    {/* ── Center: Canvas ────────────────────────────────────── */}
                    <Stack sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: 'grey.100' }}>

                        {/* Zoom bar */}
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{
                            py: 0.75, borderBottom: '1px solid', borderColor: 'divider',
                            backgroundColor: 'background.paper', flexShrink: 0,
                        }}>
                            <Tooltip title="Zoom out (scroll wheel)"><span>
                                <IconButton size="small" disabled={zoom <= MIN_ZOOM}
                                    onClick={() => setZoom((z) => Math.max(MIN_ZOOM, parseFloat((z - ZOOM_STEP).toFixed(2))))}>
                                    <ZoomOutIcon fontSize="small" />
                                </IconButton>
                            </span></Tooltip>
                            <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <Tooltip title="Zoom in (scroll wheel)"><span>
                                <IconButton size="small" disabled={zoom >= MAX_ZOOM}
                                    onClick={() => setZoom((z) => Math.min(MAX_ZOOM, parseFloat((z + ZOOM_STEP).toFixed(2))))}>
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </span></Tooltip>
                            <Button size="small" onClick={() => setZoom(1.0)}
                                sx={{ textTransform: 'none', fontSize: 11, minWidth: 0, px: 1, color: 'text.secondary' }}>
                                Reset
                            </Button>
                        </Stack>

                        {/* Scrollable canvas area */}
                        <Box ref={scrollContainerRef} sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'flex-start' }}>
                            {showPreview ? (
                                <Stack alignItems="center" spacing={1} sx={{ p: 4, width: '100%' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Live Preview (first scoring unit data)
                                    </Typography>
                                    {previewScoringUnit ? (
                                        <ScorecardCardItem
                                            template={template}
                                            scoringUnit={previewScoringUnit}
                                            eventData={previewEventData}
                                            courseData={previewCourseData}
                                            scores={previewUnitScores}
                                            markerScores={previewMarkerScores}
                                            markerLabel={previewMarkerLabel}
                                            markerScoreCode={previewMarkerScoreCode}
                                            roundValue={roundValue}
                                            blankMode={previewBlankMode}
                                            displayWidth={CANVAS_W}
                                            showBorder
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No players in event — preview unavailable.
                                        </Typography>
                                    )}
                                </Stack>
                            ) : (
                                <Box sx={{
                                    width: CANVAS_W * zoom + 80,
                                    height: CANVAS_H * zoom + 80,
                                    flexShrink: 0, position: 'relative', mx: 'auto',
                                }}>
                                    <Box
                                        ref={canvasRef}
                                        onMouseDown={handleCanvasClick}
                                        sx={{
                                            position: 'absolute', top: 40, left: 40,
                                            width: CANVAS_W, height: CANVAS_H,
                                            backgroundColor: template.backgroundColor,
                                            borderRadius: 0, boxShadow: 3,
                                            cursor: 'default', userSelect: 'none',
                                            transform: `scale(${zoom})`,
                                            transformOrigin: 'top left',
                                        }}
                                    >
                                        {template.elements.map((el) => {
                                            const isSelected = el.id === selectedId;
                                            const isHidden   = el.visible === false;
                                            const left   = (el.x      / 100) * CANVAS_W;
                                            const top    = (el.y      / 100) * CANVAS_H;
                                            const width  = (el.width  / 100) * CANVAS_W;
                                            const height = (el.height / 100) * CANVAS_H;
                                            return (
                                                <Box
                                                    key={el.id}
                                                    onMouseDown={(e) => handleElementMouseDown(e, el)}
                                                    sx={{
                                                        position: 'absolute', left, top, width, height,
                                                        border: isSelected
                                                            ? '2px solid rgba(30,78,55,0.9)'
                                                            : isHidden
                                                                ? '1px dashed rgba(0,0,0,0.35)'
                                                                : '1px dashed rgba(0,0,0,0.2)',
                                                        cursor: 'move',
                                                        overflow: 'hidden',
                                                        boxSizing: 'border-box',
                                                        opacity: isHidden ? 0.35 : 1,
                                                    }}
                                                >
                                                    <CanvasScorecardElementPreview
                                                        element={el}
                                                        template={template}
                                                        previewEventData={previewEventData}
                                                        previewScoringUnit={previewScoringUnit}
                                                        previewMarkerLabel={previewMarkerLabel}
                                                        previewMarkerScoreCode={previewMarkerScoreCode}
                                                        roundValue={roundValue}
                                                        width={width}
                                                        height={height}
                                                        uploading={uploadingIds.has(el.id)}
                                                    />
                                                    {isSelected && (
                                                        <Box
                                                            onMouseDown={(e) => handleResizeMouseDown(e, el)}
                                                            sx={{
                                                                position: 'absolute', right: 0, bottom: 0,
                                                                width: 10, height: 10, cursor: 'nwse-resize',
                                                                backgroundColor: 'rgba(30,78,55,0.9)',
                                                                borderTopLeftRadius: 2,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            );
                                        })}

                                        {/* ── Alignment guide lines (non-interactive) ────── */}
                                        {/* Horizontal: 1" from top and 1" from bottom */}
                                        {[
                                            { top: (ONE_INCH_H_PCT / 100) * CANVAS_H,       label: '1″ from top'    },
                                            { top: CANVAS_H * (1 - ONE_INCH_H_PCT / 100),   label: '1″ from bottom' },
                                        ].map(({ top, label }) => (
                                            <Box
                                                key={label}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: top - 0.5,
                                                    width: '100%',
                                                    height: 0,
                                                    borderTop: '1px dashed rgba(0, 140, 200, 0.55)',
                                                    pointerEvents: 'none',
                                                    zIndex: 9999,
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 4,
                                                        top: 1,
                                                        fontSize: 8,
                                                        lineHeight: 1,
                                                        color: 'rgba(0, 140, 200, 0.75)',
                                                        userSelect: 'none',
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {/* Vertical: horizontal center of the card */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: CANVAS_W / 2 - 0.5,
                                                width: 0,
                                                height: '100%',
                                                borderLeft: '1px dashed rgba(0, 140, 200, 0.55)',
                                                pointerEvents: 'none',
                                                zIndex: 9999,
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 4,
                                                    left: 3,
                                                    fontSize: 8,
                                                    lineHeight: 1,
                                                    color: 'rgba(0, 140, 200, 0.75)',
                                                    userSelect: 'none',
                                                    fontFamily: 'monospace',
                                                }}
                                            >
                                                center
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Size info strip */}
                        <Stack alignItems="center" sx={{
                            py: 0.5, borderTop: '1px solid', borderColor: 'divider',
                            backgroundColor: 'background.paper', flexShrink: 0,
                        }}>
                            <Typography variant="caption" color="text.secondary">
                                {CANVAS_W} × {CANVAS_H} px · 11″ × 4.25″ at print
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* ── Right: Layers + Properties ────────────────────────── */}
                    <Stack direction="row" sx={{
                        width: 480, flexShrink: 0,
                        borderLeft: '1px solid', borderColor: 'divider', overflow: 'hidden',
                    }}>
                        {/* Layers */}
                        <Stack sx={{
                            width: 160, flexShrink: 0,
                            borderRight: '1px solid', borderColor: 'divider',
                            overflow: 'hidden', display: 'flex', flexDirection: 'column',
                        }}>
                            <Stack sx={{ px: 2, pt: 2, pb: 0.5, flexShrink: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    Layers
                                </Typography>
                            </Stack>
                            <Stack sx={{ px: 1, pb: 1, flex: 1, overflowY: 'auto' }}>
                                {template.elements.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
                                        No elements yet.
                                    </Typography>
                                ) : template.elements.map((el, idx) => {
                                    const isSelected = el.id === selectedId;
                                    const isHidden   = el.visible === false;
                                    const name = elementDisplayName(el, idx, template.elements);
                                    const Icon = layerIcon(el);
                                    return (
                                        <Stack key={el.id} direction="row" spacing={0.75} alignItems="center"
                                            onClick={() => setSelectedId(el.id)}
                                            sx={{
                                                px: 1, py: 0.6, borderRadius: 1, cursor: 'pointer',
                                                backgroundColor: isSelected ? 'rgba(30,78,55,0.12)' : 'transparent',
                                                '&:hover': { backgroundColor: isSelected ? 'rgba(30,78,55,0.16)' : 'rgba(0,0,0,0.04)' },
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 15, color: isSelected ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
                                            <Typography variant="caption" sx={{
                                                flex: 1, fontWeight: isSelected ? 700 : 400,
                                                color: isSelected ? 'primary.main' : isHidden ? 'text.disabled' : 'text.primary',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {name}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateElement(el.id, { visible: el.visible === false });
                                                }}
                                                sx={{ p: 0.25, flexShrink: 0 }}
                                            >
                                                {isHidden
                                                    ? <VisibilityOff sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                    : <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />}
                                            </IconButton>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Stack>

                        {/* Properties */}
                        <Stack spacing={1.5} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            {selectedElement ? (
                                <ScorecardElementPropertiesPanel
                                    key={selectedElement.id}
                                    element={selectedElement}
                                    previewEventData={previewEventData}
                                    previewScoringUnit={previewScoringUnit}
                                    roundValue={roundValue}
                                    uploading={uploadingIds.has(selectedElement.id)}
                                    onUpdate={(patch) => updateElement(selectedElement.id, patch)}
                                    onDelete={deleteSelected}
                                    onBindingInsert={handleBindingInsert}
                                    onImageReplace={handleImageReplace}
                                    onImageLibraryOpen={() => openLibraryForReplace(selectedElement.id)}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                    Select a layer or click an element on the canvas to edit its properties.
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 'auto', flexWrap: 'wrap' }}>
                    <Tooltip title="Print current template JSON to the browser console">
                        <Button
                            size="small"
                            onClick={() => console.log(JSON.stringify(template, null, 2))}
                            sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                            Log Template
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={testPrinting || !previewScoringUnit}
                        startIcon={testPrinting ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                        onClick={handleTestPrint}
                        sx={{ textTransform: 'none' }}
                    >
                        {testPrinting ? 'Printing…' : 'Print Test'}
                    </Button>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={previewShowScores}
                                onChange={(e) => setPreviewShowScores(e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Show Scores</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={previewBlankMode}
                                onChange={(e) => setPreviewBlankMode(e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Print Blank</Typography>}
                    />
                </Stack>

                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={uploadingIds.size > 0}
                    onClick={() => onSave(template)}
                    sx={{ textTransform: 'none', px: 3 }}
                    startIcon={uploadingIds.size > 0 ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                    {uploadingIds.size > 0 ? 'Uploading images…' : 'Save Template'}
                </Button>
            </DialogActions>
        </Dialog>

        <ImageLibrary
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            mode="select"
            onSelect={handleLibrarySelect}
        />
        </>
    );
};

// ─── Canvas element preview ───────────────────────────────────────────────────

function CanvasScorecardElementPreview({
    element,
    template,
    previewEventData,
    previewScoringUnit,
    previewMarkerLabel,
    previewMarkerScoreCode,
    roundValue,
    width,
    height,
    uploading = false,
}: {
    element: ScorecardElement;
    template: ScorecardTemplate;
    previewEventData: Record<string, unknown>;
    previewScoringUnit: ScoringUnit | null;
    previewMarkerLabel: string;
    previewMarkerScoreCode: string;
    roundValue: number;
    width: number;
    height: number;
    uploading?: boolean;
}) {
    if (element.type === 'text') {
        const regRecord: Record<string, unknown> = previewScoringUnit
            ? {
                  ...(previewScoringUnit.registrations[0] as Record<string, unknown>),
                  playerName: previewScoringUnit.playerName,
                  scoreCode:  previewScoringUnit.scoreCode,
              }
            : {};
        const resolved = resolveScorecardBindings(element.content ?? '', regRecord, previewEventData, roundValue, {
            markerName: previewMarkerLabel,
            markerScoreCode: previewMarkerScoreCode,
        });
        return (
            <Typography sx={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: element.fontSize ?? 11,
                fontWeight: element.fontWeight ?? 'normal',
                color: element.color ?? '#000000',
                textAlign: element.textAlign ?? 'left',
                lineHeight: 1.3, px: 0.25,
                wordBreak: 'break-word', width: '100%',
            }}>
                {resolved || element.content || '(empty)'}
            </Typography>
        );
    }

    if (element.type === 'image') {
        if (!element.imageDataUrl) {
            return (
                <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
                    <ImageOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                </Stack>
            );
        }
        return (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <img src={element.imageDataUrl} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: uploading ? 0.4 : 1 }} />
                {uploading && (
                    <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
                        <CircularProgress size={16} />
                    </Stack>
                )}
            </Box>
        );
    }

    if (element.type === 'qrcode') {
        return (
            <Stack alignItems="center" justifyContent="center"
                sx={{ width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <QrCode2Icon sx={{ fontSize: Math.min(width, height) * 0.6, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontSize: 8, color: 'text.secondary', mt: 0.25 }}>QR Code</Typography>
            </Stack>
        );
    }

    if (element.type === 'line') {
        const color = element.lineColor ?? '#000000';
        const isH   = element.lineOrientation !== 'vertical';
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{
                    width:  isH ? '100%' : Math.max((element.lineThickness ?? 1), 1),
                    height: isH ? Math.max(height, 1) : '100%',
                    backgroundColor: color,
                }} />
            </Box>
        );
    }

    if (element.type === 'scorecardGrid') {
        const cfg = element.gridConfig;
        if (!cfg) return null;
        const sf = width / SCORECARD_CANVAS_W;
        const regRecord: Record<string, unknown> = previewScoringUnit
            ? {
                  ...(previewScoringUnit.registrations[0] as Record<string, unknown>),
                  playerName: previewScoringUnit.playerName,
                  scoreCode:  previewScoringUnit.scoreCode,
              }
            : {};
        const highlightHole = templateUsesStartingHoleBinding(template)
            ? resolveHighlightHoleNumber(template, regRecord, previewEventData, roundValue) ?? 1
            : null;
        return (
            <ScorecardGrid
                config={cfg}
                teeSet={null}
                playerScores={[]}
                playerLabels={['AB']}
                markerScores={[]}
                markerLabel=""
                playerCount={1}
                scaleFactor={sf}
                isSampleData={true}
                highlightHoleNumber={highlightHole}
            />
        );
    }

    return null;
}

// ─── Deferred number field ────────────────────────────────────────────────────

interface DeferredNumberFieldProps {
    label: string;
    value: number;
    min?: number;
    max?: number;
    onCommit: (val: number) => void;
    sx?: object;
}

function DeferredNumberField({ label, value, min = -Infinity, max = Infinity, onCommit, sx }: DeferredNumberFieldProps) {
    const [local, setLocal] = React.useState(String(Math.round(value)));
    const focused = React.useRef(false);

    React.useEffect(() => {
        if (!focused.current) setLocal(String(Math.round(value)));
    }, [value]);

    const commit = React.useCallback(() => {
        const num = parseFloat(local);
        if (local.trim() === '' || isNaN(num)) { setLocal(String(Math.round(value))); return; }
        const clamped = clamp(num, min, max);
        setLocal(String(Math.round(clamped)));
        onCommit(clamped);
    }, [local, value, min, max, onCommit]);

    return (
        <TextField label={label} type="number" size="small" value={local}
            inputProps={{ min, max, step: 1 }} sx={sx}
            onChange={(e) => setLocal(e.target.value)}
            onFocus={() => { focused.current = true; }}
            onBlur={() => { focused.current = false; commit(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
        />
    );
}

// ─── Properties panel ────────────────────────────────────────────────────────

interface PropertiesPanelProps {
    element: ScorecardElement;
    previewEventData: Record<string, unknown>;
    previewScoringUnit: ScoringUnit | null;
    roundValue: number;
    uploading?: boolean;
    onUpdate: (patch: Partial<ScorecardElement>) => void;
    onDelete: () => void;
    onBindingInsert: (token: string) => void;
    onImageReplace: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onImageLibraryOpen: () => void;
}

function ScorecardElementPropertiesPanel({
    element,
    uploading = false,
    onUpdate,
    onDelete,
    onBindingInsert,
    onImageReplace,
    onImageLibraryOpen,
}: PropertiesPanelProps) {
    const [unitMode, setUnitMode] = React.useState<'%' | 'px'>('%');

    const typeLabel =
        element.type === 'text'          ? 'Text Field' :
        element.type === 'image'         ? 'Image' :
        element.type === 'qrcode'        ? 'QR Code' :
        element.type === 'line'          ? 'Line' :
        element.type === 'scorecardGrid' ? 'Scorecard Grid' :
        'Element';

    const toDisplay = (pct: number, axis: 'x' | 'y') => {
        if (unitMode === '%') return Math.round(pct);
        return Math.round((pct / 100) * (axis === 'x' ? CANVAS_W : CANVAS_H));
    };
    const fromInput = (val: number, axis: 'x' | 'y') => {
        if (unitMode === '%') return val;
        return (val / (axis === 'x' ? CANVAS_W : CANVAS_H)) * 100;
    };

    // Grid config helper
    const cfg = element.gridConfig;
    const patchCfg = (cfgPatch: Partial<ScorecardGridConfig>) => {
        if (!cfg) return;
        onUpdate({ gridConfig: { ...cfg, ...cfgPatch } });
    };

    return (
        <Stack spacing={1.5}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {typeLabel} Properties
                </Typography>
                <Tooltip title="Delete element">
                    <IconButton size="small" onClick={onDelete} color="error">
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Divider />

            {/* Position & size */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Position &amp; Size
                </Typography>
                <ToggleButtonGroup size="small" exclusive value={unitMode}
                    onChange={(_, val) => { if (val) setUnitMode(val); }} sx={{ height: 22 }}>
                    <ToggleButton value="%" sx={{ px: 1, py: 0, fontSize: 11, textTransform: 'none' }}>%</ToggleButton>
                    <ToggleButton value="px" sx={{ px: 1, py: 0, fontSize: 11, textTransform: 'none' }}>px</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1}>
                <DeferredNumberField label={`X (${unitMode})`} value={toDisplay(element.x, 'x')}
                    min={0} max={unitMode === '%' ? 100 : CANVAS_W}
                    onCommit={(v) => onUpdate({ x: clamp(fromInput(v, 'x'), 0, 100) })} sx={{ width: '50%' }} />
                <DeferredNumberField label={`Y (${unitMode})`} value={toDisplay(element.y, 'y')}
                    min={0} max={unitMode === '%' ? 100 : CANVAS_H}
                    onCommit={(v) => onUpdate({ y: clamp(fromInput(v, 'y'), 0, 100) })} sx={{ width: '50%' }} />
            </Stack>
            <Stack direction="row" spacing={1}>
                <DeferredNumberField label={`W (${unitMode})`} value={toDisplay(element.width, 'x')}
                    min={1} max={unitMode === '%' ? 100 : CANVAS_W}
                    onCommit={(v) => onUpdate({ width: clamp(fromInput(v, 'x'), 0.5, 100) })} sx={{ width: '50%' }} />
                <DeferredNumberField label={`H (${unitMode})`} value={toDisplay(element.height, 'y')}
                    min={1} max={unitMode === '%' ? 100 : CANVAS_H}
                    onCommit={(v) => onUpdate({ height: clamp(fromInput(v, 'y'), 0.5, 100) })} sx={{ width: '50%' }} />
            </Stack>

            <Divider />

            {/* ── text ─────────────────────────────────────────────────────── */}
            {element.type === 'text' && (
                <>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Content</Typography>
                    <TextField label="Text" size="small" multiline minRows={2}
                        value={element.content ?? ''} onChange={(e) => onUpdate({ content: e.target.value })} fullWidth />

                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Insert Data Binding</Typography>
                    <Select size="small" displayEmpty value=""
                        onChange={(e: SelectChangeEvent<string>) => { if (e.target.value) onBindingInsert(e.target.value); }}
                        renderValue={() => 'Insert field…'}>
                        {SCORECARD_BINDING_TOKENS.map(({ token, label }) => (
                            <MenuItem key={token} value={token}>
                                {label}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{token}</Typography>
                            </MenuItem>
                        ))}
                    </Select>

                    <Divider />

                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Style</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField label="Size" type="number" size="small"
                            value={element.fontSize ?? 11} onChange={(e) => onUpdate({ fontSize: Number(e.target.value) })}
                            inputProps={{ min: 6, max: 72 }} sx={{ width: '45%' }} />
                        <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: 0.5, border: '1px solid rgba(0,0,0,0.25)', backgroundColor: element.color ?? '#000000', flexShrink: 0 }} />
                            <Typography variant="caption">Color</Typography>
                            <input type="color" value={element.color ?? '#000000'}
                                onChange={(e) => onUpdate({ color: e.target.value })}
                                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                        </Box>
                    </Stack>
                    <ToggleButtonGroup size="small" exclusive value={element.textAlign ?? 'left'}
                        onChange={(_, val: TextAlign | null) => { if (val) onUpdate({ textAlign: val }); }}>
                        <ToggleButton value="left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup size="small" exclusive value={element.fontWeight ?? 'normal'}
                        onChange={(_, val) => { if (val) onUpdate({ fontWeight: val }); }}>
                        <ToggleButton value="normal" sx={{ fontSize: 12, textTransform: 'none' }}>Regular</ToggleButton>
                        <ToggleButton value="bold" sx={{ fontSize: 12, fontWeight: 700, textTransform: 'none' }}>Bold</ToggleButton>
                    </ToggleButtonGroup>
                </>
            )}

            {/* ── image ────────────────────────────────────────────────────── */}
            {element.type === 'image' && (
                <>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Image</Typography>
                        {uploading && <><CircularProgress size={10} /><Typography variant="caption" color="text.secondary">Uploading…</Typography></>}
                    </Stack>
                    {element.imageDataUrl && (
                        <Box sx={{ position: 'relative' }}>
                            <Box component="img" src={element.imageDataUrl} alt="preview"
                                sx={{ width: '100%', maxHeight: 80, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider', opacity: uploading ? 0.4 : 1 }} />
                            {uploading && <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}><CircularProgress size={18} /></Stack>}
                        </Box>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" size="small" component="label" disabled={uploading}
                            startIcon={uploading ? <CircularProgress size={12} /> : <AddIcon />}
                            sx={{ textTransform: 'none', flex: 1 }}>
                            {uploading ? 'Uploading…' : element.imageDataUrl ? 'Replace' : 'Upload'}
                            <input type="file" accept="image/*" hidden onChange={onImageReplace} />
                        </Button>
                        <Button variant="outlined" size="small" disabled={uploading}
                            startIcon={<ImageOutlinedIcon fontSize="small" />}
                            onClick={onImageLibraryOpen} sx={{ textTransform: 'none', flex: 1 }}>
                            Library
                        </Button>
                    </Stack>
                </>
            )}

            {/* ── qrcode ───────────────────────────────────────────────────── */}
            {element.type === 'qrcode' && (
                <>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>QR Code</Typography>
                    <Typography variant="caption" color="text.secondary">
                        The live-scoring URL (score code + event ID) is automatically embedded per card at print time.
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>Score Code Target</Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={element.qrTarget ?? 'player'}
                        onChange={(_, val) => { if (val) onUpdate({ qrTarget: val }); }}
                    >
                        <ToggleButton value="player" sx={{ fontSize: 11, textTransform: 'none' }}>Player</ToggleButton>
                        <ToggleButton value="marker" sx={{ fontSize: 11, textTransform: 'none' }}>Marker</ToggleButton>
                    </ToggleButtonGroup>
                </>
            )}

            {/* ── line ─────────────────────────────────────────────────────── */}
            {element.type === 'line' && (
                <>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Line Style</Typography>
                    <ToggleButtonGroup size="small" exclusive value={element.lineOrientation ?? 'horizontal'}
                        onChange={(_, val) => { if (val) onUpdate({ lineOrientation: val }); }}>
                        <ToggleButton value="horizontal" sx={{ fontSize: 11, textTransform: 'none' }}>Horizontal</ToggleButton>
                        <ToggleButton value="vertical"   sx={{ fontSize: 11, textTransform: 'none' }}>Vertical</ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField label="Thickness (px)" type="number" size="small"
                            value={element.lineThickness ?? 1}
                            onChange={(e) => onUpdate({ lineThickness: clamp(Number(e.target.value), 1, 8) })}
                            inputProps={{ min: 1, max: 8 }} sx={{ width: '55%' }} />
                        <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: 0.5, border: '1px solid rgba(0,0,0,0.25)', backgroundColor: element.lineColor ?? '#000000', flexShrink: 0 }} />
                            <Typography variant="caption">Color</Typography>
                            <input type="color" value={element.lineColor ?? '#000000'}
                                onChange={(e) => onUpdate({ lineColor: e.target.value })}
                                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                        </Box>
                    </Stack>
                </>
            )}

            {/* ── scorecardGrid ─────────────────────────────────────────────── */}
            {element.type === 'scorecardGrid' && cfg && (
                <>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Grid Role</Typography>
                    <FormControl size="small">
                        <RadioGroup row value={cfg.gridRole}
                            onChange={(e) => patchCfg({ gridRole: e.target.value as 'player' | 'marker' })}>
                            <FormControlLabel value="player" control={<Radio size="small" />} label={<Typography variant="caption">Player</Typography>} />
                            <FormControlLabel value="marker" control={<Radio size="small" />} label={<Typography variant="caption">Marker</Typography>} />
                        </RadioGroup>
                    </FormControl>

                    <Divider />

                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Show Rows</Typography>
                    {([
                        { key: 'showPar',      label: 'Par' },
                        { key: 'showHandicap', label: 'Handicap' },
                        { key: 'showDistance', label: 'Distance' },
                        { key: 'showPlayer',   label: 'Player Scores' },
                        { key: 'showMarker',   label: 'Marker Scores' },
                        { key: 'showStrokes',  label: 'Stroke Dots' },
                    ] as const).map(({ key, label }) => (
                        <FormControlLabel key={key}
                            control={<Checkbox size="small" checked={cfg[key]} onChange={(e) => patchCfg({ [key]: e.target.checked })} />}
                            label={<Typography variant="caption">{label}</Typography>}
                            sx={{ ml: 0, '& .MuiFormControlLabel-label': { lineHeight: 1 } }}
                        />
                    ))}

                    {cfg.showPlayer && (
                        <>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Player Rows</Typography>
                            <FormControl size="small">
                                <RadioGroup row value={cfg.playerRows === 'auto' ? 'auto' : 'fixed'}
                                    onChange={(e) => patchCfg({ playerRows: e.target.value === 'auto' ? 'auto' : 1 })}>
                                    <FormControlLabel value="auto" control={<Radio size="small" />} label={<Typography variant="caption">Auto (per unit)</Typography>} />
                                    <FormControlLabel value="fixed" control={<Radio size="small" />} label={<Typography variant="caption">Fixed</Typography>} />
                                </RadioGroup>
                            </FormControl>
                            {cfg.playerRows !== 'auto' && (
                                <TextField label="Row count" type="number" size="small"
                                    value={cfg.playerRows}
                                    onChange={(e) => patchCfg({ playerRows: clamp(Number(e.target.value), 1, 6) })}
                                    inputProps={{ min: 1, max: 6 }} sx={{ width: '50%' }} />
                            )}
                        </>
                    )}

                    <TextField
                        label="Row height (px)"
                        type="number"
                        size="small"
                        placeholder="auto"
                        value={cfg.playerRowHeightPx ?? ''}
                        onChange={(e) => {
                            const raw = e.target.value;
                            patchCfg({ playerRowHeightPx: raw === '' ? undefined : clamp(Number(raw), 10, 80) });
                        }}
                        inputProps={{ min: 10, max: 80 }}
                        sx={{ width: '60%' }}
                        helperText="Applies to player & marker rows. Leave blank for auto."
                    />

                    <Divider />

                    <FormControlLabel
                        control={<Checkbox size="small" checked={cfg.showFoldGap} onChange={(e) => patchCfg({ showFoldGap: e.target.checked })} />}
                        label={<Typography variant="caption">Fold Gap</Typography>}
                        sx={{ ml: 0 }}
                    />
                    {cfg.showFoldGap && (
                        <TextField label="Gap width (px)" type="number" size="small"
                            value={cfg.foldGapPx}
                            onChange={(e) => patchCfg({ foldGapPx: clamp(Number(e.target.value), 4, 60) })}
                            inputProps={{ min: 4, max: 60 }} sx={{ width: '60%' }} />
                    )}

                    {cfg.gridRole === 'marker' && (
                        <>
                            <Divider />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Tear-Away Position
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Constrains this grid within 1″ of the selected edge.
                            </Typography>
                            <FormControl size="small">
                                <RadioGroup value={cfg.markerPosition ?? 'none'}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'none') {
                                            patchCfg({ markerPosition: undefined });
                                        } else {
                                            const mp = val as 'top' | 'bottom';
                                            const newY = mp === 'top'
                                                ? 0
                                                : clamp(100 - ONE_INCH_H_PCT, 0, 100 - element.height);
                                            patchCfg({ markerPosition: mp });
                                            onUpdate({ gridConfig: { ...cfg, markerPosition: mp }, y: newY });
                                        }
                                    }}>
                                    <FormControlLabel value="none"   control={<Radio size="small" />} label={<Typography variant="caption">None</Typography>} />
                                    <FormControlLabel value="top"    control={<Radio size="small" />} label={<Typography variant="caption">Top (within 1″)</Typography>} />
                                    <FormControlLabel value="bottom" control={<Radio size="small" />} label={<Typography variant="caption">Bottom (within 1″)</Typography>} />
                                </RadioGroup>
                            </FormControl>
                        </>
                    )}
                </>
            )}
        </Stack>
    );
}
